import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { User } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  logout: (callback: () => void) => void;
  signOut: (callback: () => void) => void;
  register: (email: string, password: string, name: string, callback: () => void) => Promise<void>;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ADMIN_EMAILS = [
  '@admin.com',
  'fincap30@gmail.com',
  'info@tradeco.bz',
  'admin@tradeco.bz',
  'support@tradeco.bz'
];

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const isAdmin = user?.email ? ADMIN_EMAILS.some(adminEmail => 
    adminEmail.includes('@') 
      ? user.email === adminEmail 
      : user.email?.endsWith(adminEmail)
  ) : false;

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      console.log('Current user:', session?.user);
      console.log('Is admin?', isAdmin);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      console.log('Auth state changed:', session?.user);
      console.log('Is admin?', isAdmin);
    });

    return () => subscription.unsubscribe();
  }, []);

  const register = async (email: string, password: string, name: string, callback: () => void) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          username: name,
        },
      },
    });
    if (error) throw error;
    callback();
  };

  const logout = async (callback: () => void) => {
    await supabase.auth.signOut();
    callback();
  };

  // Add signOut as an alias for logout
  const signOut = logout;

  return (
    <AuthContext.Provider value={{ user, logout, signOut, register, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};